import { apiClient } from '@/services/core/apiClient';
import type { ApiResponse } from '@/services/core/apiClient';

// Auth-related types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: number;
    email: string;
    name: string;
    userType: string;
  };
  hasuraToken?: string;
}

export interface SignOutResponse {
  message: string;
}

// Auth API service
export const authApi = {
  /**
   * Sign in user with email and password
   * Matches original ApiClient.signIn behavior
   */
  async signIn(credentials: LoginCredentials): Promise<any> {
    const response = await apiClient.post<any>(
      '/auth/signin',
      credentials
    );

    if (response.success && response.data) {
      console.log('Login successful:', response.data);
      // Return the raw data to match original behavior
      return response.data;
    } else {
      console.error('Login failed:', response.error);
      
      // Create an error that preserves the original response structure for LoginForm
      const error = new Error(response.error || 'Sign in failed');
      // Attach the response data to match what LoginForm expects
      (error as any).response = {
        data: response.data || { errors: [response.error || 'Sign in failed'] }
      };
      throw error;
    }
  },

  /**
   * Sign out current user
   */
  async signOut(): Promise<ApiResponse<SignOutResponse>> {
    const response = await apiClient.post<SignOutResponse>('/auth/signout');

    if (response.success) {
      console.log('Logout successful');
    } else {
      console.error('Logout failed:', response.error);
    }

    return response;
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(token: string): Promise<ApiResponse<{ token: string }>> {
    const response = await apiClient.post<{ token: string }>(
      '/auth/refresh',
      {},
      token
    );

    if (response.success) {
      console.log('Token refreshed successfully');
    } else {
      console.error('Token refresh failed:', response.error);
    }

    return response;
  },

  /**
   * Verify token validity
   */
  async verifyToken(token: string): Promise<ApiResponse<{ valid: boolean }>> {
    const response = await apiClient.get<{ valid: boolean }>(
      '/auth/verify',
      token
    );

    return response;
  }
};

export default authApi;